import { PaletteMode, ThemeOptions } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'dark'
      ? {
          primary: {
            main: '#00ff8c',
            dark: '#00c674',
          },
          secondary: {
            main: '#383838',
          },
          success: {
            main: '#00ff8c',
          },
        }
      : {
          primary: {
            main: '#00c674',
            // dark: '#00c674',
          },
          secondary: {
            main: '#eeeeee',
          },
          success: {
            main: '#00ff8c',
          },
        }),
  },
  typography: {
    h1: { fontSize: '2.375' },
    h2: { fontSize: '1.875' },
    h3: { fontSize: '1.25rem' },
    h4: { fontSize: '1rem' },
    subtitle1: { fontSize: '1rem' },
    subtitle2: { fontSize: '0.875rem' },
    body1: { fontSize: '1rem' },
    body2: { fontSize: '0.875rem' },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: '0.625em',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          fontWeight: 600,
          fontSize: ownerState.size == 'small' ? '.875rem' : '1rem',
          boxShadow: 'none',
          borderRadius: theme.spacing(1),
          textTransform: 'none',
          borderWidth: 2,
          ...theme.unstable_sx({
            px:
              ownerState.size == 'small'
                ? theme.spacing(1)
                : ownerState.size == 'large'
                ? theme.spacing(3)
                : theme.spacing(2),
            py:
              ownerState.size == 'small'
                ? theme.spacing(0)
                : ownerState.size == 'large'
                ? theme.spacing(2)
                : theme.spacing(1),
          }),
          ...(ownerState.variant != 'outlined' && {
            borderWidth: 2,
            borderColor: 'transparent',
          }),
          ...(ownerState.variant == 'outlined' &&
            ownerState.color == 'primary' &&
            mode == 'light' && {
              color: '#00c674 !important',
            }),
        }),
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          boxShadow: 'none',
          '& .MuiButton-root': {
            boxShadow: 'none',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: theme.spacing(1),
          borderWidth: 5,
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(5px)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(5px)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '& .MuiTabs-flexContainer': {
            alignItems: 'stretch',
            height: '100%',
            padding: theme.spacing(0.5),
            gap: theme.spacing(1),
          },
          '& .MuiTab-root:not(.MuiTab-fullWidth)': {
            width: 'auto',
            padding: theme.spacing(0, 2),
          },
          '& .MuiTab-root': {
            borderRadius: 4,
            minHeight: '1em',
            height: '100%',
            fontWeight: 'bold',
            textTransform: 'none',
            color: 'inherit',
            transition: `all ${theme.transitions.duration.short}ms`,
          },
          '& .MuiTab-root:hover': {
            backgroundColor: mode == 'dark' ? '#ffffff14' : '#00000014',
          },
          '& .Mui-selected': {
            backgroundColor: theme.palette.action.hover,
            color: mode == 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: theme.spacing(1),
          ...(ownerState.variant == 'outlined' && {
            borderWidth: 2,
            borderColor: theme.palette[ownerState.severity || 'success'].main,
            color: theme.palette[ownerState.severity || 'success'].main,
          }),
        }),
      },
    },
  },
});
